(ns frontend.ui.page.lp-report-editor.step-2-data-gathering.modal.private-material-bulk-upload-modal
  (:require
   [common.localization :as loc]
   [frontend.ui.component.file-input.view :as file-input]
   [frontend.ui.component.modal-dialog.view :as modal-dialog]
   [frontend.ui.page.lp-report-editor.common.style :as common-style]
   [frontend.ui.page.lp-report-editor.step-2-data-gathering.event :as event]
   [frontend.ui.page.lp-report-editor.step-2-data-gathering.style :as style]
   [frontend.ui.page.lp-report-editor.step-2-data-gathering.sub :as sub]
   [herb.core :as h]
   [re-frame.core :as rf]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.loading-spinner.view :as spinner]
   [ui.components.select.select :as select]
   [ui.components.radix-ui.tooltip.view :as tooltip]))

(defn- close-button
  []
  [button/secondary-hollow
   {:on-click #(rf/dispatch [::event/close-bulk-upload])}
   (loc/tr :close)])

(defn- selected-commitment-ids
  [files]
  (->> files
       (mapcat :selected-commitment-ids)
       (set)))

(defn- cleanup-commitments
  [files commitment-options]
  (let [selected-ids (selected-commitment-ids files)]
    (->> commitment-options
         (filter (fn [{:keys [commitment-id materials]}]
                   (and (or (empty? selected-ids)
                            (contains? selected-ids commitment-id))
                        (seq materials))))
         (vec))))

(defn- commitment-option-import-id
  [{:keys [private-file-import-id common/external-id]}]
  (or private-file-import-id external-id))

(defn- commitment-option-label
  [{:keys [investor-name] :as commitment-option}]
  (if-let [import-id (some-> (commitment-option-import-id commitment-option)
                             (not-empty))]
    (str investor-name " (" import-id ")")
    investor-name))

(defn- file-row
  [{:keys [id name]}]
  [:div
   {:class (h/<class style/private-material-bulk-upload-file-row)}
   [:div
    {:class (h/<class style/private-material-bulk-upload-file-name)}
    name]
   [:button
    {:class    (h/<class style/private-material-bulk-upload-remove-button)
     :type     "button"
     :title    (loc/tr :remove-file)
     :on-click #(rf/dispatch [::event/remove-bulk-upload-file id])}
    [:i.fal.fa-trash-alt]]])

(defn- upload-step
  [files commitment-options]
  (let [cleanup-commitments (cleanup-commitments [] commitment-options)]
    [:<>
     [:div
      {:class (h/<class style/private-material-bulk-upload-description-row)}
      [:div
       {:class (h/<class style/private-material-bulk-upload-description)}
       (loc/tr :upload-files-in-bulk-description)]
      [tooltip/view
       {:side :right}
       [:span
        {:class (h/<class style/private-material-bulk-upload-info-icon)}
        [:i.fas.fa-info-circle]]
       (loc/tr :upload-files-in-bulk-tooltip)]]
     [:div
      {:class (h/<class style/private-material-bulk-upload-dropzone)}
      [file-input/file-input
       {:input-id        "private-material-bulk-upload"
        :file-types      [".xlsx" ".pdf"]
        :multiple?       true
        :handle-add-file #(rf/dispatch [::event/add-bulk-upload-file %])}]]
     (when (seq files)
       [:div
        {:class (h/<class style/private-material-bulk-upload-files-wrapper)}
        [:div
         {:class (h/<class style/private-material-bulk-upload-files-title)}
         (loc/tr :uploaded-documents-list)]
        [:div
         {:class (h/<class style/private-material-bulk-upload-files-list)}
         (->> files
              (map (fn [file]
                     ^{:key (str "bulk-private-material-file-" (:id file))}
                     [file-row file]))
              (doall))]])
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-footer)}
      [:div
       {:class (h/<class common-style/footer-left)}
       [close-button]
       (when (seq cleanup-commitments)
         [button/secondary-hollow
          {:on-click #(rf/dispatch [::event/open-bulk-upload-cleanup-step
                                    :upload])}
          (loc/tr :manage-existing-attachments)])]
      [:div
       {:class (h/<class common-style/footer-right)}
       [button/primary-solid
        {:disabled? (empty? files)
         :on-click  #(rf/dispatch [::event/next-bulk-upload-step
                                   commitment-options])}
        (loc/tr :next)]]]]))

(defn- review-row
  [file commitment-options]
  (let [match-source (:match-source file)
        match-status (cond
                       (= match-source :import-id) {:label-key :matched-by-option-id
                                                    :successful? true}
                       (= match-source :investor-name) {:label-key :matched-by-option-investor-name
                                                        :successful? true}
                       (= match-source :manual) {:label-key :matched-by-option-manual-selection
                                                 :successful? true}
                       :else {:label-key :matched-by-option-no-match
                              :successful? false})]
    [:div
     {:class (h/<class style/private-material-bulk-upload-review-row)}
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-cell)}
      [:div (:name file)]]
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-match-cell)}
      [:i
       {:class (str (if (:successful? match-status)
                      "fas fa-check "
                      "fas fa-times ")
                    (h/<class (if (:successful? match-status)
                                style/private-material-bulk-upload-match-icon-success
                                style/private-material-bulk-upload-match-icon-failure)))}]
      [:span
       {:class (h/<class (if (:successful? match-status)
                           style/private-material-bulk-upload-match-text-success
                           style/private-material-bulk-upload-match-text-neutral))}
       (loc/tr (:label-key match-status))]]
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-select-cell)}
      [select/view
       {:is-multi?          true
        :class              "w-full min-w-0 h-auto flex-wrap"
        :menu-placement     "auto"
        :menu-portal-target (.-body js/document)
        :value              (:selected-commitment-ids file)
        :on-change          (fn [selected-values _selected-options]
                              (rf/dispatch [::event/select-bulk-upload-commitment
                                            (:id file)
                                            selected-values]))
        :options            commitment-options}]]]))

(defn- review-step
  [files commitment-options]
  (let [commitment-select-options (->> commitment-options
                                       (mapv (fn [{:keys [commitment-id] :as option}]
                                               {:value commitment-id
                                                :label (commitment-option-label option)})))
        cleanup-commitments (cleanup-commitments files commitment-options)
        matched-file-count (count (filter (comp seq :selected-commitment-ids) files))
        selected-files? (some (comp seq :selected-commitment-ids) files)]
    [:<>
     [:div
      {:class (h/<class style/private-material-bulk-upload-description)}
      (loc/tr :upload-files-in-bulk-review-description)]
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-summary)}
      (loc/tr :private-material-bulk-upload-matched-count
              [matched-file-count (count files)])]
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-table)}
      [:div
       {:class (h/<class style/private-material-bulk-upload-review-header)}
       [:div
        {:class (h/<class style/private-material-bulk-upload-review-header-cell)}
        (loc/tr :filename)]
       [:div
        {:class (h/<class style/private-material-bulk-upload-review-match-header-cell)}
        (loc/tr :matched-by)]
       [:div
        {:class (h/<class style/private-material-bulk-upload-review-header-cell)}
        (loc/tr :investor)]]
      [:div
       {:class (h/<class style/private-material-bulk-upload-review-rows)}
       (->> files
            (map (fn [file]
                   ^{:key (str "bulk-private-material-review-file-" (:id file))}
                   [review-row file commitment-select-options]))
            (doall))]]
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-footer)}
      [:div
       {:class (h/<class common-style/footer-left)}
       [close-button]
       [button/secondary-hollow
        {:on-click #(rf/dispatch [::event/back-bulk-upload-step])}
        (loc/tr :back)]
       (when (seq cleanup-commitments)
         [button/secondary-hollow
          {:on-click #(rf/dispatch [::event/open-bulk-upload-cleanup-step
                                    :review])}
          (loc/tr :manage-existing-attachments)])]
      [:div
       {:class (h/<class common-style/footer-right)}
       [button/primary-solid
        {:disabled? (not selected-files?)
         :on-click  #(rf/dispatch [::event/finish-bulk-upload-review
                                   commitment-options])}
        (loc/tr :finish)]]]]))

(defn- cleanup-material-item
  [attachment remove-existing-attachment-ids]
  (let [attachment-id (:db/id attachment)
        marked? (contains? remove-existing-attachment-ids attachment-id)]
    [:div
     {:class (h/<class style/private-material-bulk-upload-cleanup-material-item)}
     [:div
      {:class (h/<class style/private-material-bulk-upload-cleanup-material-name)}
      (:attachment/filename attachment)]
     [button/secondary-hollow
      {:on-click #(rf/dispatch [::event/toggle-bulk-upload-existing-attachment
                                attachment-id])}
      (loc/tr (if marked?
                :keep
                :remove))]]))

(defn- cleanup-row
  [{:keys [commitment-id investor-name materials]} remove-existing-attachment-ids]
  [:div
   {:class (h/<class style/private-material-bulk-upload-cleanup-row)}
   [:div
    {:class (h/<class style/private-material-bulk-upload-cleanup-investor-cell)}
    investor-name]
   [:div
    {:class (h/<class style/private-material-bulk-upload-cleanup-files-cell)}
    (->> materials
         (map (fn [attachment]
                ^{:key (str "bulk-private-material-cleanup-material-"
                            commitment-id
                            "-"
                            (:db/id attachment))}
                [cleanup-material-item attachment remove-existing-attachment-ids]))
         (doall))]])

(defn- cleanup-step
  [{:keys [files commitment-options remove-existing-attachment-ids cleanup-error-message]}]
  (let [cleanup-commitments (cleanup-commitments files commitment-options)]
    [:<>
     [:div
      {:class (h/<class style/private-material-bulk-upload-description)}
      (loc/tr :private-material-bulk-upload-cleanup-description)]
     (when cleanup-error-message
       [:div
        {:class "mb-4"}
        [alert/danger {}
         (if (keyword? cleanup-error-message)
           (loc/tr cleanup-error-message)
           cleanup-error-message)]])
     [:div
      {:class (h/<class style/private-material-bulk-upload-cleanup-actions)}
      [button/secondary-hollow
       {:on-click #(rf/dispatch [::event/remove-all-bulk-upload-existing-attachments
                                 commitment-options])}
       (loc/tr :remove-all)]]
     [:div
      {:class (h/<class style/private-material-bulk-upload-cleanup-table)}
      [:div
       {:class (h/<class style/private-material-bulk-upload-cleanup-header)}
       [:div
        {:class (h/<class style/private-material-bulk-upload-cleanup-header-cell)}
        (loc/tr :investor)]
       [:div
        {:class (h/<class style/private-material-bulk-upload-cleanup-header-cell)}
        (loc/tr :file)]]
      [:div
       {:class (h/<class style/private-material-bulk-upload-cleanup-rows)}
       (->> cleanup-commitments
            (map (fn [commitment]
                   ^{:key (str "bulk-private-material-cleanup-row-"
                               (:commitment-id commitment))}
                   [cleanup-row commitment remove-existing-attachment-ids]))
            (doall))]]
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-footer)}
      [:div
       {:class (h/<class common-style/footer-left)}
       [close-button]
       [button/secondary-hollow
        {:on-click #(rf/dispatch [::event/back-bulk-upload-step])}
        (loc/tr :back)]]
      [:div
       {:class (h/<class common-style/footer-right)}
       [button/primary-solid
        {:on-click #(rf/dispatch [::event/start-bulk-upload-progress
                                  commitment-options])}
        (loc/tr :continue)]]]]))

(defn- progress-step
  [{:keys [files pending-removals progress-mode progress-total-files progress-total-removals
           cancel-requested?]}]
  (let [target-files (->> files
                          (filter :selected-commitment-id)
                          (vec))
        processed-files (->> target-files
                             (filter (fn [{:keys [status]}]
                                       (contains? #{:complete :error} status)))
                             (count))
        current-removal (first pending-removals)
        processed-removals (- (or progress-total-removals 0)
                              (count pending-removals))
        current-file (->> target-files
                          (filter (fn [{:keys [status]}]
                                    (contains? #{:uploading :processing} status)))
                          (first))
        total-operations (+ (or progress-total-files 0)
                            (or progress-total-removals 0))
        processed-operations (+ processed-files
                                processed-removals)
        removing? (and (= progress-mode :cleanup-only)
                       (or current-removal
                           (pos? progress-total-removals)))
        progress-percentage (if (pos? total-operations)
                              (int (* 100 (/ processed-operations total-operations)))
                              0)]
    [:div
     {:class (h/<class style/private-material-bulk-upload-progress)}
     [spinner/circle]
     [:div
      {:class (h/<class style/private-material-bulk-upload-progress-title)}
      (loc/tr (if removing?
                :private-material-bulk-upload-removing-title
                :uploading))]
     [:div
      {:class (h/<class style/private-material-bulk-upload-progress-description)}
      (loc/tr (if removing?
                :private-material-bulk-upload-removing-description
                :private-material-bulk-upload-progress-description))]
     [:div
      {:class (h/<class style/private-material-bulk-upload-progress-count)}
      (loc/tr :private-material-bulk-upload-progress-count
              [processed-operations total-operations])]
     [:div
      {:class (h/<class style/private-material-bulk-upload-progress-bar-wrapper)}
      [:div
       {:class (h/<class common-style/progress)}
       [:div
        {:class (h/<class (partial common-style/progress-bar progress-percentage))}
        (str progress-percentage "%")]]]
     (when (or current-removal current-file)
       [:div
        {:class (h/<class style/private-material-bulk-upload-progress-current-file)}
        (loc/tr (if removing?
                  :private-material-bulk-upload-progress-current-attachment
                  :private-material-bulk-upload-progress-current-file)
                [(or (:attachment-name current-removal)
                     (:name current-file))])])
     [:div
      {:class (h/<class style/private-material-bulk-upload-review-footer)}
      [:div
       {:class (h/<class common-style/footer-left)}
       [button/secondary-hollow
        {:disabled? cancel-requested?
         :on-click  #(rf/dispatch [::event/open-bulk-upload-stop-dialog])}
        (loc/tr (if cancel-requested?
                  :private-material-bulk-upload-stopping
                  :stop-processing))]]]]))

(defn view
  [commitment-options]
  (let [{:keys [cleanup-error-message files step remove-existing-attachment-ids stop-processing-dialog-visible?]
         :as bulk-upload}
        @(rf/subscribe [::sub/private-material-bulk-upload])]
    [:<>
     [modal-dialog/modal-dialog
      {:visible?     (seq @(rf/subscribe [::sub/private-material-bulk-upload]))
       :hide!        (if (= step :progress)
                       #(rf/dispatch [::event/open-bulk-upload-stop-dialog])
                       #(rf/dispatch [::event/close-bulk-upload]))
       :header-label (loc/tr :upload-files-in-bulk-title)
       :size         1000}
      (case step
        :progress [progress-step bulk-upload]
        :cleanup [cleanup-step
                  {:files files
                   :commitment-options commitment-options
                   :remove-existing-attachment-ids remove-existing-attachment-ids
                   :cleanup-error-message cleanup-error-message}]
        :review [review-step files commitment-options]
        [upload-step files commitment-options])]
     [modal-dialog/confirm-dialog
      {:visible?           stop-processing-dialog-visible?
       :size               :xs
       :hide!              #(rf/dispatch [::event/close-bulk-upload-stop-dialog])
       :header-label       (loc/tr :stop-processing)
       :confirm-text       (loc/tr :private-material-bulk-upload-stop-processing-confirm-text)
       :ok-button-text     (loc/tr :private-material-bulk-upload-stop-after-current-item)
       :cancel-button-text (loc/tr :continue-processing)
       :on-confirm         #(rf/dispatch [::event/confirm-bulk-upload-stop])
       :on-cancel          #(rf/dispatch [::event/close-bulk-upload-stop-dialog])}]]))
